import {DeleteOutlined, EditOutlined, ExperimentOutlined, EyeOutlined, FileTextOutlined, HeartOutlined, HomeOutlined, MedicineBoxOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, SolutionOutlined, WomanOutlined} from '@ant-design/icons';
import {Button, Card, Collapse, Descriptions, Drawer, Empty, Input, Popconfirm, Space, Tag, Tooltip, message} from 'antd';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';

import {TableData} from '../../../../CommonComponents/TableData';
import {RQDeletePatientHistory, RQLookupAllPatientHistory} from '../../../../ReactQuery/apis/rqPatientHistory';

import AddPatienthistory from './AddPatientHistory/AddPatientHistory';
import {EditPatientHistory} from './EditPatientHistory';

const emptyText = value => (value || value === 0 ? value : <span className={'text-gray-400'}>—</span>);

const TagList = ({items, color = 'blue', max = 3}) => {
  if (!items?.length) {return <span className={'text-gray-400'}>—</span>;}
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;

  return (
    <Space size={4} wrap>
      {shown.map(item => <Tag key={item} color={color} className={'m-0'}>{item}</Tag>)}
      {extra > 0 && <Tag className={'m-0'}>+{extra} more</Tag>}
    </Space>
  );
};

TagList.propTypes = {color: PropTypes.string, items: PropTypes.array, max: PropTypes.number};

// ─── History Detail Drawer (scoped to one patient) ────────────────────────────
const HistoryDetailDrawer = ({history, patientName, onClose}) => {
  const items = history ? [
    {
      key: 'examination',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><MedicineBoxOutlined /> Examination</span>,
      children: (
        <Descriptions column={2} size={'small'} bordered>
          <Descriptions.Item label={'Pulse'}>{emptyText(history.examination?.pulse)}</Descriptions.Item>
          <Descriptions.Item label={'BP'}>{emptyText(history.examination?.bp)}</Descriptions.Item>
          <Descriptions.Item label={'Height'}>{emptyText(history.examination?.height)}</Descriptions.Item>
          <Descriptions.Item label={'Weight'}>{emptyText(history.examination?.weight)}</Descriptions.Item>
          <Descriptions.Item label={'BMI'}>{emptyText(history.examination?.bmi)}</Descriptions.Item>
          <Descriptions.Item label={'Temperature'}>{emptyText(history.examination?.temperature)}</Descriptions.Item>
          <Descriptions.Item label={'Nadi'}>{emptyText(history.examination?.nadi)}</Descriptions.Item>
          <Descriptions.Item label={'Urine'}>{emptyText(history.examination?.urine)}</Descriptions.Item>
          <Descriptions.Item label={'Stools'}>{emptyText(history.examination?.stools)}</Descriptions.Item>
          <Descriptions.Item label={'Hunger'}>{emptyText(history.examination?.hunger)}</Descriptions.Item>
          <Descriptions.Item label={'Sleep'}>{emptyText(history.examination?.sleep)}</Descriptions.Item>
          <Descriptions.Item label={'Tongue'}>{emptyText(history.examination?.tongue)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'menstrual',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><WomanOutlined /> Menstrual History</span>,
      children: (
        <Descriptions column={2} size={'small'} bordered>
          <Descriptions.Item label={'Menarche'}>{emptyText(history.menstrualHistory?.menarche)}</Descriptions.Item>
          <Descriptions.Item label={'LMP'}>{emptyText(history.menstrualHistory?.lmp)}</Descriptions.Item>
          <Descriptions.Item label={'UPT'}>
            {history.menstrualHistory?.upt === undefined
              ? emptyText()
              : <Tag color={history.menstrualHistory.upt ? 'green' : 'default'}>{history.menstrualHistory.upt ? 'Positive' : 'Negative'}</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label={'Cycle Length'}>{emptyText(history.menstrualHistory?.cycleLength)}</Descriptions.Item>
          <Descriptions.Item label={'Flow'}>{emptyText(history.menstrualHistory?.flow)}</Descriptions.Item>
          <Descriptions.Item label={'Regularity'}>{emptyText(history.menstrualHistory?.regularOrIrregular)}</Descriptions.Item>
          <Descriptions.Item label={'Color'}>{emptyText(history.menstrualHistory?.color)}</Descriptions.Item>
          <Descriptions.Item label={'Clots'}>{emptyText(history.menstrualHistory?.clots)}</Descriptions.Item>
          <Descriptions.Item label={'Discharge'}>{emptyText(history.menstrualHistory?.discharge)}</Descriptions.Item>
          <Descriptions.Item label={'Pain'}>{emptyText(history.menstrualHistory?.pain)}</Descriptions.Item>
          <Descriptions.Item label={'Bloating'}>{emptyText(history.menstrualHistory?.bloating)}</Descriptions.Item>
          <Descriptions.Item label={'Breast Tenderness'}>{emptyText(history.menstrualHistory?.breastTenderness)}</Descriptions.Item>
          <Descriptions.Item label={'Mood Changes'} span={2}>{emptyText(history.menstrualHistory?.moodChanges)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'obstetric',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><HeartOutlined /> Obstetric History</span>,
      children: (
        <Descriptions column={2} size={'small'} bordered>
          <Descriptions.Item label={'Married Since'}>{emptyText(history.obstetricHistory?.marriedSince)}</Descriptions.Item>
          <Descriptions.Item label={'Previous Pregnancy'}>{emptyText(history.obstetricHistory?.previousPregnancy)}</Descriptions.Item>
          <Descriptions.Item label={'Miscarriages'}>{emptyText(history.obstetricHistory?.miscarriages)}</Descriptions.Item>
          <Descriptions.Item label={'Ectopic Pregnancy'}>{emptyText(history.obstetricHistory?.ectopicPregnancy)}</Descriptions.Item>
          <Descriptions.Item label={'IVF / IUI History'} span={2}>{emptyText(history.obstetricHistory?.ivfOrIuiHistory)}</Descriptions.Item>
          <Descriptions.Item label={'Ovulation Tracking'} span={2}>{emptyText(history.obstetricHistory?.ovulationTracking)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'lifestyle',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><HomeOutlined /> Lifestyle</span>,
      children: (
        <Descriptions column={2} size={'small'} bordered>
          <Descriptions.Item label={'Agni'}>{emptyText(history.lifestyle?.agni)}</Descriptions.Item>
          <Descriptions.Item label={'Diet'}>{emptyText(history.lifestyle?.diet)}</Descriptions.Item>
          <Descriptions.Item label={'Exercise'}>{emptyText(history.lifestyle?.exercise)}</Descriptions.Item>
          <Descriptions.Item label={'Breakfast'}>{emptyText(history.lifestyle?.breakfast)}</Descriptions.Item>
          <Descriptions.Item label={'Lunch'}>{emptyText(history.lifestyle?.lunch)}</Descriptions.Item>
          <Descriptions.Item label={'Evening'}>{emptyText(history.lifestyle?.evening)}</Descriptions.Item>
          <Descriptions.Item label={'Dinner'}>{emptyText(history.lifestyle?.dinner)}</Descriptions.Item>
          <Descriptions.Item label={'Mood Changes'} span={2}>{emptyText(history.lifestyle?.moodChanges)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'investigation',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><ExperimentOutlined /> Investigation</span>,
      children: <TagList items={history.investigation?.investigations} color={'geekblue'} max={20} />,
    },
    {
      key: 'diagnosis',
      label: <span className={'flex items-center gap-2 font-medium text-primary-700'}><SolutionOutlined /> Diagnosis</span>,
      children: (
        <Descriptions column={1} size={'small'} bordered>
          <Descriptions.Item label={'Diagnosis'}>{emptyText(history.diagnosis?.diagnosis)}</Descriptions.Item>
          <Descriptions.Item label={'Doctor Remarks'}>{emptyText(history.diagnosis?.doctorRemarks)}</Descriptions.Item>
        </Descriptions>
      ),
    },
  ] : [];

  return (
    <Drawer
      open={!!history}
      onClose={onClose}
      width={800}
      title={(
        <span className={'text-primary-700 font-bold text-base flex items-center gap-2'}>
          <FileTextOutlined /> Patient History — {patientName}
        </span>
      )}
      destroyOnHidden
    >
      {history && (
        <div>
          <Card size={'small'} className={'mb-4 bg-primary-50 border-primary-100'}>
            <Descriptions column={1} size={'small'}>
              <Descriptions.Item label={'Created Date'}>{emptyText(history.createdDate)}</Descriptions.Item>
              <Descriptions.Item label={'Symptoms'}>
                <TagList items={history.symptoms} color={'orange'} max={20} />
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Collapse items={items} defaultActiveKey={['examination']} className={'bg-white'} />
        </div>
      )}
    </Drawer>
  );
};

HistoryDetailDrawer.propTypes = {
  history: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  patientName: PropTypes.string,
};

// ─── History Section (scoped to this patient) 
const PatientHistory = ({patientId, patientName, token}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editHistory, setEditHistory] = useState(null);
  const [detailHistory, setDetailHistory] = useState(null);
  const [searchText, setSearchText] = useState('');

  const {data: allHistories = [], isFetching: loading, refetch} = RQLookupAllPatientHistory(token);
  const deletePatientHistory = RQDeletePatientHistory();

  const histories = useMemo(() => {
    let list = allHistories.filter(h => String(h.patientId) === String(patientId));

    list = [...list].sort((a, b) => new Date(b.createdDate ?? 0) - new Date(a.createdDate ?? 0));

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();

      list = list.filter(h =>
        h.diagnosis?.diagnosis?.toLowerCase().includes(q)
        || (h.symptoms || []).some(s => s.toLowerCase().includes(q)));
    }

    return list;
  }, [allHistories, patientId, searchText]);

  const withDiagnosisCount = useMemo(() => histories.filter(h => h.diagnosis?.diagnosis).length, [histories]);
  const latestVisit = histories[0]?.createdDate;

  const handleDelete = async id => {
    try {
      await deletePatientHistory.mutateAsync({id, token});
      message.success('Patient history deleted');
      refetch();
    } catch {
      message.error('Delete failed');
    }
  };

  const columns = [
    {title: 'Sr. No.', key: 'srno', width: 70, render: (_, __, index) => index + 1},
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
      render: d => (
        <button
          onClick={() => setDetailHistory(histories.find(h => h.createdDate === d) || histories[0])}
          className={'hover:text-primary-800 font-semibold text-left hover:underline bg-transparent border-0 cursor-pointer p-0'}
        >
          {d ? new Date(d).toLocaleDateString() : '—'}
        </button>
      ),
    },
    {title: 'Symptoms', dataIndex: 'symptoms', render: symptoms => <TagList items={symptoms} color={'orange'} />},
    {title: 'BP', dataIndex: ['examination', 'bp'], render: v => emptyText(v)},
    {title: 'BMI', dataIndex: ['examination', 'bmi'], render: v => emptyText(v)},
    {
      title: 'Diagnosis',
      dataIndex: ['diagnosis', 'diagnosis'],
      render: v => (v ? <Tag color={'green'} className={'m-0'}>{v}</Tag> : <Tag className={'m-0'}>Pending</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title={'View Details'}>
            <Button size={'small'} icon={<EyeOutlined />} onClick={() => setDetailHistory(rec)} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
          </Tooltip>
          <Tooltip title={'Edit'}>
            <Button size={'small'} icon={<EditOutlined />} onClick={() => setEditHistory(rec)} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
          </Tooltip>
          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Delete this patient history?'}
              onConfirm={() => handleDelete(rec.patienthistoryId)}
              okButtonProps={{danger: true}}
              okText={'Delete'}
            >
              <Button size={'small'} icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* <div className={'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'}>
        <StatCard icon={<FileTextOutlined />} label={'Total Records'} value={histories.length}  />
        <StatCard icon={<SolutionOutlined />} label={'With Diagnosis'} value={withDiagnosisCount} colorClass={'bg-primary-50'} />
        <StatCard icon={<CalendarOutlined />} label={'Latest Visit'} value={latestVisit ? new Date(latestVisit).toLocaleDateString() : '—'}  />
      </div> */}

      <div className={'mb-5 flex flex-wrap gap-3 items-center justify-between'}>
        <div className={'flex flex-wrap gap-3 items-center'}>
          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by symptom or diagnosis…'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{width: 280}}
          />
          {searchText.trim() && <Tag color={'blue'} className={'text-xs'}>{histories.length} result{histories.length !== 1 ? 's' : ''}</Tag>}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading} className={'btn-secondary'}>Refresh</Button>
          <Button type={'primary'} icon={<PlusOutlined />} onClick={() => setAddOpen(true)} className={'btn-primary'}>Add History</Button>
        </Space>
      </div>

      <Card className={'rounded-lg border border-primary-100 shadow-sm'} bodyStyle={{padding: 0}}>
        <TableData
          dataSource={histories}
          columns={columns}
          rowKey={'patienthistoryId'}
          loading={loading}
          size={'middle'}
          scroll={{x: 900}}
          rowClassName={'hover:bg-primary-50 transition-colors cursor-pointer'}
          //   onRow={rec => ({onClick: () => setDetailHistory(rec)})}
          locale={{emptyText: <Empty description={'No history recorded for this patient yet'} />}}
        />
      </Card>

      <AddPatienthistory
        open={addOpen}
        onClose={() => setAddOpen(false)}
        token={token}
        onCreated={refetch}
        defaultPatientId={patientId}
      />

      <EditPatientHistory
        open={!!editHistory}
        onClose={() => setEditHistory(null)}
        history={editHistory}
        token={token}
        onUpdated={refetch}
      />

      <HistoryDetailDrawer history={detailHistory} patientName={patientName} onClose={() => setDetailHistory(null)} />
    </div>
  );
};

PatientHistory.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientName: PropTypes.string,
  token: PropTypes.string,
};

export default PatientHistory ;
